import type {Icon} from '@primer/octicons-react'
import {
  CommentDiscussionIcon,
  CopyIcon,
  GitPullRequestIcon,
  ArchiveIcon,
  LockIcon,
  PackageIcon,
  PencilIcon,
  TrashIcon,
  UnlockIcon,
} from '@primer/octicons-react'
import {useCallback, useMemo} from 'react'
import {copyText} from '@github-ui/copy-to-clipboard'
import {sendEvent} from '@github-ui/hydro-analytics'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {SessionState} from '../types/session'
import type {Pull} from '../types/pull'
import {extractSpecTaskThreadIdFromEventIdentifiers} from '../utils/extract-spec-task-thread-id'
import {COPILOT_CHAT_SPEC_TASK_EVENT_TYPE} from '../utils/constants'
import {generateTaskPathForRepo} from '../utils/generate-task-path'
import {useIntentDetectionEnabled} from '../contexts/IntentDetectionEnabledContext'
import {useArchiveDialogOptional} from '../contexts/ArchiveDialogContext'
import {useDeleteTaskDialogOptional} from '../contexts/DeleteTaskDialogContext'
import {useTaskSharingDialogOptional} from '../contexts/TaskSharingDialogContext'
import {useUnarchiveTask} from './use-unarchive-task'
import {useArchiveTask} from './use-archive-task'

export interface TaskActionItem {
  key: string
  icon: Icon
  label: string
  href?: string
  variant?: 'danger'
  onSelect: (event?: React.SyntheticEvent) => void
}

export interface UseTaskActionItemsProps {
  state?: string
  taskId?: string
  pull?: Pull
  repoOwner?: string
  repoName?: string
  currentUserIsOwner?: boolean
  archivedAt?: string | null
  eventType?: string | null
  eventIdentifiers?: string[] | null
  hideCopy?: boolean
  hidePull?: boolean
  hideArchive?: boolean
  hideShare?: boolean
  hideDelete?: boolean
  interactiveSession?: boolean
  agentSlug?: string
  sharingStatus?: 'unshared' | 'shared'
  onRename?: () => void
}

export function isSessionCompleted(state?: string): boolean {
  if (!state) return false
  return [SessionState.Completed, SessionState.Failed, SessionState.Cancelled].map(s => s.toLowerCase()).includes(state)
}

export function canUnarchive(props: UseTaskActionItemsProps): boolean {
  const {archivedAt, pull, taskId, currentUserIsOwner, interactiveSession, hideArchive} = props
  if (hideArchive) return false
  if (!archivedAt || !taskId) return false
  if (interactiveSession) return true
  return !!(pull?.state !== 'merged' && currentUserIsOwner)
}

export function hasConversationAction(props: Pick<UseTaskActionItemsProps, 'eventType' | 'eventIdentifiers'>): boolean {
  const isTaskPlanLinksEnabled = isFeatureEnabled('copilot_immersive_task_hyperlinking')
  if (props.eventType !== COPILOT_CHAT_SPEC_TASK_EVENT_TYPE || !isTaskPlanLinksEnabled) return false
  const threadId = extractSpecTaskThreadIdFromEventIdentifiers(props.eventIdentifiers)
  return !!threadId
}

export function useTaskActionItems(props: UseTaskActionItemsProps): {
  items: TaskActionItem[]
  hasAnyAction: boolean
} {
  const {
    state,
    taskId,
    pull,
    repoOwner,
    repoName,
    currentUserIsOwner,
    archivedAt,
    eventType,
    eventIdentifiers,
    hideCopy = false,
    hidePull = false,
    hideArchive = false,
    hideShare = false,
    hideDelete = false,
    interactiveSession = false,
    agentSlug,
    sharingStatus,
    onRename,
  } = props

  const archiveDialog = useArchiveDialogOptional()
  const deleteDialog = useDeleteTaskDialogOptional()
  const sharingDialog = useTaskSharingDialogOptional()
  const {isUsingIntentDetection} = useIntentDetectionEnabled()
  const {mutate: unarchiveTask} = useUnarchiveTask({
    taskId,
    repoNwo: repoOwner && repoName ? `${repoOwner}/${repoName}` : undefined,
  })
  const {mutate: archiveTask} = useArchiveTask({
    taskId,
    repoOwner,
    repoName,
  })

  const sessionCompleted = isSessionCompleted(state)
  const conversationThreadId = extractSpecTaskThreadIdFromEventIdentifiers(eventIdentifiers)
  const conversationHref = conversationThreadId ? `/copilot/c/${conversationThreadId}` : undefined
  const showConversation = hasConversationAction({eventType, eventIdentifiers})
  const showUnarchive = canUnarchive(props)

  const handleCopyLink = useCallback(
    async (event?: React.SyntheticEvent) => {
      try {
        const path = generateTaskPathForRepo({
          repositoryNwo: repoOwner && repoName ? `${repoOwner}/${repoName}` : undefined,
          pullRequestNumber: pull?.number,
          taskId,
          isUsingIntentDetection,
        })
        const fullUrl = `${window.location.origin}${path}`
        await copyText(fullUrl)
        sendEvent('agent_sessions.task_actions', {
          target: 'COPY_TASK_LINK',
          task_id: taskId ?? pull?.global_relay_id,
        })
      } catch {
        event?.preventDefault()
        event?.stopPropagation()
      }
    },
    [repoOwner, repoName, taskId, pull, isUsingIntentDetection],
  )

  const handleArchive = useCallback(() => {
    if (!taskId) return

    if (pull?.state === 'open' && archiveDialog) {
      archiveDialog.openArchiveDialog(taskId, repoOwner, repoName, pull)
    } else {
      archiveTask()
    }
  }, [taskId, repoOwner, repoName, pull, archiveDialog, archiveTask])

  const handleUnarchive = useCallback(() => {
    unarchiveTask()
  }, [unarchiveTask])

  const handleShareSession = useCallback(() => {
    if (!taskId || !sharingDialog) return
    sharingDialog.openSharingDialog(taskId, repoOwner, repoName, false)
  }, [taskId, repoOwner, repoName, sharingDialog])

  const handleUnshareSession = useCallback(() => {
    if (!taskId || !sharingDialog) return
    sharingDialog.openSharingDialog(taskId, repoOwner, repoName, true)
  }, [taskId, repoOwner, repoName, sharingDialog])

  const handleDeleteSession = useCallback(() => {
    if (!taskId || !deleteDialog) return
    deleteDialog.openDeleteDialog(taskId, repoOwner, repoName)
  }, [taskId, repoOwner, repoName, deleteDialog])

  const items = useMemo(() => {
    const result: TaskActionItem[] = []

    if (onRename && taskId && currentUserIsOwner) {
      result.push({
        key: 'rename',
        icon: PencilIcon,
        label: 'Rename',
        onSelect: onRename,
      })
    }

    if (
      !hideShare &&
      sharingDialog &&
      taskId &&
      currentUserIsOwner &&
      repoOwner &&
      repoName &&
      isFeatureEnabled('copilot_mission_control_task_sharing')
    ) {
      if (sharingStatus === 'shared') {
        result.push({
          key: 'sharing-settings',
          icon: UnlockIcon,
          label: 'Sharing settings',
          onSelect: handleUnshareSession,
        })
      } else {
        result.push({
          key: 'sharing-settings',
          icon: LockIcon,
          label: 'Sharing settings',
          onSelect: handleShareSession,
        })
      }
    }

    if (!hideCopy && ((repoOwner && repoName && (taskId || pull?.number)) || taskId)) {
      result.push({
        key: 'copy-link',
        icon: CopyIcon,
        label: 'Copy link',
        onSelect: handleCopyLink,
      })
    }

    if (!hidePull && pull?.url) {
      result.push({
        key: 'view-pr',
        icon: GitPullRequestIcon,
        label: 'View pull request',
        href: pull.url,
        onSelect: () => {
          sendEvent('dotcom_chat.activate', {
            target: 'TASK_ACTION_VIEW_PR',
            mode: 'immersive',
          })
        },
      })
    }

    if (showConversation && conversationHref) {
      result.push({
        key: 'go-to-conversation',
        icon: CommentDiscussionIcon,
        label: 'Go to parent conversation',
        href: conversationHref,
        onSelect: () => {
          sendEvent('dotcom_chat.activate', {
            target: 'TASK_ACTION_GO_TO_CONVERSATION',
            mode: 'immersive',
          })
        },
      })
    }

    if (!hideArchive && (sessionCompleted || interactiveSession) && !archivedAt && taskId && currentUserIsOwner) {
      result.push({
        key: 'archive-session',
        icon: ArchiveIcon,
        label: 'Archive session',
        onSelect: handleArchive,
      })
    }

    if (showUnarchive) {
      result.push({
        key: 'unarchive-session',
        icon: PackageIcon,
        label: 'Unarchive session',
        onSelect: handleUnarchive,
      })
    }

    if (!hideDelete && deleteDialog && taskId && currentUserIsOwner && agentSlug === 'copilot-developer-cli') {
      result.push({
        key: 'delete-session',
        icon: TrashIcon,
        label: 'Delete',
        variant: 'danger',
        onSelect: handleDeleteSession,
      })
    }

    return result
  }, [
    hideCopy,
    hideArchive,
    hidePull,
    hideShare,
    hideDelete,
    sharingStatus,
    repoOwner,
    repoName,
    pull,
    taskId,
    showConversation,
    conversationHref,
    sessionCompleted,
    interactiveSession,
    agentSlug,
    archivedAt,
    currentUserIsOwner,
    showUnarchive,
    handleCopyLink,
    handleArchive,
    handleUnarchive,
    handleShareSession,
    handleUnshareSession,
    handleDeleteSession,
    sharingDialog,
    deleteDialog,
    onRename,
  ])

  return {items, hasAnyAction: items.length > 0}
}
